import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, TrendingUp } from 'lucide-react';
import { getAuthLink } from '@/lib/links';

export function ROICalculatorMonday() {
  const [monthlyJobs, setMonthlyJobs] = useState(12);
  const [avgTicket, setAvgTicket] = useState(14000);

  // Conservative trade metrics:
  // 1. 24/7 reception recovers ~15% of leads that previously went to voicemail
  const recoveredJobsPerMonth = Math.max(1, Math.round(monthlyJobs * 0.15));
  const recoveredRevenue = recoveredJobsPerMonth * avgTicket;
  // 2. Average roofer spends 2.5 hrs per job on phone tags, measurements, and invoice chasing
  const hoursSavedPerMonth = monthlyJobs * 2.5;
  const displayCapacityPercent = Math.max(24, Math.min(100, Math.round((monthlyJobs / 60) * 100)));

  return (
    <section className="py-20 bg-[#F9FAFB] border-t border-gray-100">
      <div className="max-w-[1360px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl sm:text-[48px] lg:text-[56px] font-normal tracking-[-0.03em] text-[#000000] leading-[1.12]">
            Calculate your office savings
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#323338] font-normal">
            See what happens when you stop losing after-hours calls to voicemail and automate
            satellite estimates.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto bg-white rounded-3xl border border-gray-200 p-6 sm:p-10 shadow-md grid grid-cols-1 md:grid-cols-12 gap-8 items-center"
        >
          {/* Controls */}
          <div className="md:col-span-7 flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-bold text-[#181B20]">
                  Completed Roof Jobs / Month:
                </span>
                <span className="text-base font-extrabold text-[#7950A2]">{monthlyJobs}</span>
              </div>
              <input
                type="range"
                min="4"
                max="60"
                step="1"
                value={monthlyJobs}
                onChange={(e) => setMonthlyJobs(Number(e.target.value))}
                aria-label="Monthly completed roof jobs"
                className="w-full accent-[#7950A2] cursor-pointer"
              />
              <div className="flex justify-between text-[11px] text-[#6B7280] mt-1">
                <span>4 jobs (Solo)</span>
                <span>30 jobs (Growing)</span>
                <span>60+ jobs (Commercial)</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-bold text-[#181B20]">Average Replacement Value:</span>
                <span className="text-base font-extrabold text-[#7950A2]">
                  ${avgTicket.toLocaleString()}
                </span>
              </div>
              <input
                type="range"
                min="8000"
                max="30000"
                step="1000"
                value={avgTicket}
                onChange={(e) => setAvgTicket(Number(e.target.value))}
                aria-label="Average roof replacement value"
                className="w-full accent-[#7950A2] cursor-pointer"
              />
              <div className="flex justify-between text-[11px] text-[#6B7280] mt-1">
                <span>$8,000 (Shingle)</span>
                <span>$18,000 (Architectural)</span>
                <span>$30,000+ (Metal/Tile)</span>
              </div>
            </div>

            {/* Animated Capacity Gauge */}
            <div className="p-4 rounded-2xl bg-[#FAFAFC] border border-gray-200/80 space-y-2.5">
              <div className="flex items-center justify-between text-xs font-bold text-[#181B20]">
                <span className="flex items-center gap-1.5">
                  <TrendingUp className="size-3.5 text-[#7950A2]" />
                  AI Fleet Automation Coverage
                </span>
                <span className="text-[#7950A2]">{displayCapacityPercent}% capacity</span>
              </div>
              <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  initial={false}
                  animate={{ width: `${displayCapacityPercent}%` }}
                  transition={{ type: 'spring', stiffness: 220, damping: 24 }}
                  className="h-full bg-gradient-to-r from-[#7950A2] to-[#F7C76C] rounded-full"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1 text-xs text-[#4B5563]">
                <div className="flex items-center gap-1.5">
                  <Check className="size-3.5 text-[#00C875] shrink-0" />
                  <span>Replaces EagleView report fees</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="size-3.5 text-[#00C875] shrink-0" />
                  <span>Replaces 24/7 call services</span>
                </div>
              </div>
            </div>
          </div>

          {/* Results Summary Box */}
          <motion.div
            key={`${monthlyJobs}-${avgTicket}`}
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 340, damping: 22 }}
            className="md:col-span-5 bg-[#F4F2F7] rounded-2xl p-6 border border-[#E5DEEE] flex flex-col justify-between shadow-xs"
          >
            <span className="text-xs font-bold uppercase tracking-wider text-[#7950A2] block mb-1">
              Estimated Monthly Impact
            </span>

            <div className="mb-4">
              <span className="text-3xl sm:text-4xl font-extrabold text-[#181B20] tracking-tight block">
                +${recoveredRevenue.toLocaleString()}
              </span>
              <span className="text-xs text-[#6B7280]">
                in recovered jobs ({recoveredJobsPerMonth} extra roof won / mo)
              </span>
            </div>

            <div className="mb-6 pt-3 border-t border-[#E5DEEE]">
              <span className="text-xl font-bold text-[#181B20] block">
                {hoursSavedPerMonth} hours
              </span>
              <span className="text-xs text-[#6B7280]">
                saved in takeoffs, phone calls, and billing
              </span>
            </div>

            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href={getAuthLink('/register')}
              className="inline-flex min-h-11 items-center justify-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm bg-[#7950A2] text-white hover:bg-[#68418f] transition-all shadow-xs"
            >
              <span>Start Free Trial</span>
              <ArrowRight className="size-4" />
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
